// Team permissions — the console's view of "who may do what".
//
// One table (backend/shared/entities/policyDefaults.js PERMISSIONS, copied
// into entityRegistry.gen.json) names every tunable action with a default
// minimum role. The effective role is org.permissions[key] → config/app
// permissions[key] → default — the same resolution the generated Firestore
// rules (permRoles) and the backend routes (requireOrgRole with a key) use,
// so a button that renders here is a write that passes there.
//
//   const can = useCan(org, myRole);
//   {can('agent.create') && <button>Create agent</button>}
//
// Reach for a key, not roleSatisfies(myRole, 'admin'): a literal role in the
// UI is a deploy to change who sees the button; a key is an org admin's
// edit in Administration → Permissions.
import { useCallback } from 'react';
import registry from './entityRegistry.gen.json';
import { ROLES, roleSatisfies } from './entities';
import { useLiveConfig } from './firestore';

export const PERMISSIONS = registry.permissions || [];
export const DEFAULT_PERMISSIONS = Object.fromEntries(PERMISSIONS.map((p) => [p.key, p.role]));

export function permissionRole(key, org = null, config = null) {
  const o = org?.permissions?.[key];
  if (ROLES.includes(o)) return o;
  const c = config?.permissions?.[key];
  if (ROLES.includes(c)) return c;
  return DEFAULT_PERMISSIONS[key] || 'owner';
}

export function can(key, roleHeld, org = null, config = null) {
  return roleSatisfies(roleHeld, permissionRole(key, org, config));
}

// Hook form: subscribes to config/app so a platform-wide change lands live.
export function useCan(org, myRole) {
  const { doc: config } = useLiveConfig();
  return useCallback((key) => can(key, myRole, org, config), [myRole, org, config]);
}
